import classNames from "classnames";
import filesize from "filesize";
import React from "react";
import { hasValue, hasValueFn, isNodeEmpty } from "Tools";
import { SimpleFieldProps, useSimpleFieldProps } from "./FormTools";
import { ErrorMessage, YupDefined } from "./Tools";

export function FileInput(props: {
  field: SimpleFieldProps<File | null>;
  validFileTypes?: string[];
  filename?: string;
  maxSizeBytes?: number;
  isDisabled?: boolean;
  children?: React.ReactNode;
}) {
  const field = useSimpleFieldProps(props.field);
  const validate = (value: File | null) => {
    if (
      value &&
      hasValue(props.maxSizeBytes) &&
      value.size > props.maxSizeBytes
    ) {
      // Accept in more lenient units of MiB, but display as MB since some users might not know what a MiB is
      return `This file exceeds the allowed limit. Please select a file that is no greater than ${filesize(
        props.maxSizeBytes,
        { base: 2, standard: "jedec" },
      )}.`;
    }
  };
  const filename = field.value ? field.value.name : props.filename;
  return (
    <div>
      <div className={classNames("file", { "has-name": filename })}>
        <label className="file-label">
          <input
            className="file-input"
            type="file"
            id={field.name}
            name={field.name}
            accept={hasValueFn(props.validFileTypes, v => v.join(","))}
            onChange={async e => {
              const file = e.target.files?.[0] ?? null;
              const error = validate(file);
              if (hasValue(error)) {
                await field.onError?.(error);
                e.target.value = "";
              } else {
                await field.onChange(file);
              }
              await field.onBlur(file, false);
            }}
            disabled={props.isDisabled}
            {...(!field.value && { value: "" })}
          />
          <span
            className="file-cta"
            style={{
              height: "unset",
              whiteSpace: "unset",
            }}
          >
            <span className="file-icon">
              <i className="fa fa-upload" />
            </span>
            <span
              className="file-label"
              style={{
                display: "unset",
              }}
            >
              {!isNodeEmpty(props.children) ? (
                props.children
              ) : (
                <>Choose a file…</>
              )}
            </span>
          </span>
          {hasValue(filename) && <span className="file-name">{filename}</span>}
        </label>
      </div>
      <ErrorMessage>{field.error}</ErrorMessage>
    </div>
  );
}

FileInput.getDefaultValidator = () => YupDefined.ofType<File | null>();
